import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  HelpCircle,
  RotateCcw,
  SkipForward,
  Trophy,
  XCircle
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useToast } from '../components/ui/ToastProvider'
import { GLOSSARY_QUIZ_QUESTIONS } from '../data/glossaryQuizQuestions'
import {
  GlossaryQuizQuestion,
  PersistedQuizSession,
  QuizAnswer,
  QuizCategory,
  QuizPhase,
  QuizResult
} from '../types/glossaryQuiz'
import { trackQuizEvent } from '../utils/quizAnalytics'
import { clearQuizSession, loadQuizSession, saveQuizSession } from '../utils/quizStorage'

const QUESTION_POOL_COUNT = 25
const SESSION_QUESTION_COUNT = 25
const ROUND_ONE_COUNT = 10

const shuffleArray = <T,>(items: T[]): T[] => {
  const copiedItems = [...items]
  for (let i = copiedItems.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = copiedItems[i]
    copiedItems[i] = copiedItems[j]
    copiedItems[j] = temp
  }
  return copiedItems
}

const categoryLabels: Record<QuizCategory, string> = {
  core_terminology: 'Core Terminology Fundamentals',
  volume_and_revenue: 'Volume and Revenue Definitions',
  yield_metrics: 'Yield Metrics (RPM Family)',
  per_event_metrics: 'Per-event Metrics',
  rate_metrics: 'Rate Metrics and Denominator Context',
  dashboard_concepts: 'Dashboard Concepts',
  identity_and_privacy: 'Identity and Privacy Rules'
}

const buildSessionQuestionIds = (questions: GlossaryQuizQuestion[]): string[] => {
  const coreRound = shuffleArray(questions.filter((question) => question.round === 'core10'))
  const bonusRound = shuffleArray(questions.filter((question) => question.round === 'bonus15'))

  if (coreRound.length === ROUND_ONE_COUNT && coreRound.length + bonusRound.length >= SESSION_QUESTION_COUNT) {
    return [...coreRound, ...bonusRound].slice(0, SESSION_QUESTION_COUNT).map((question) => question.id)
  }

  return shuffleArray(questions).slice(0, SESSION_QUESTION_COUNT).map((question) => question.id)
}

const getPerformanceBand = (scorePercent: number): QuizResult['performanceBand'] => {
  if (scorePercent >= 90) {
    return 'Strong understanding'
  }
  if (scorePercent >= 70) {
    return 'Good understanding, minor gaps'
  }
  if (scorePercent >= 50) {
    return 'Needs review in selected categories'
  }
  return 'Recommend full glossary review'
}

const validateQuestionBank = (questions: GlossaryQuizQuestion[]): string[] => {
  const errors: string[] = []

  if (questions.length !== QUESTION_POOL_COUNT) {
    errors.push(`Question bank must contain exactly ${QUESTION_POOL_COUNT} questions. Found ${questions.length}.`)
  }

  const seenIds = new Set<string>()
  questions.forEach((question) => {
    if (seenIds.has(question.id)) {
      errors.push(`Duplicate question ID detected: ${question.id}`)
    }
    seenIds.add(question.id)

    if (question.correctOptionIndex < 0 || question.correctOptionIndex >= question.options.length) {
      errors.push(`Question ${question.id} has an invalid correctOptionIndex.`)
    }
  })

  return errors
}

const computeResult = (
  servedQuestionIds: string[],
  questionMap: Record<string, GlossaryQuizQuestion>,
  answers: QuizAnswer[],
  strictMode: boolean
): QuizResult => {
  const servedSet = new Set(servedQuestionIds)
  const servedAnswers = answers.filter((answer) => servedSet.has(answer.questionId))

  const totalCorrect = servedAnswers.filter((answer) => answer.isCorrect).length
  const totalSkipped = servedAnswers.filter((answer) => answer.skipped).length
  const totalWrong = servedAnswers.length - totalCorrect - totalSkipped

  const rawScore = Number((totalCorrect - (strictMode ? totalWrong * 0.25 : 0)).toFixed(2))
  const percentageScore = servedQuestionIds.length > 0
    ? Number(((rawScore / servedQuestionIds.length) * 100).toFixed(2))
    : 0

  const categoryTotals = (Object.keys(categoryLabels) as QuizCategory[])
    .reduce<Record<QuizCategory, { asked: number; correct: number }>>((accumulator, category) => {
      accumulator[category] = { asked: 0, correct: 0 }
      return accumulator
    }, {} as Record<QuizCategory, { asked: number; correct: number }>)

  servedQuestionIds.forEach((questionId) => {
    const question = questionMap[questionId]
    if (question) {
      categoryTotals[question.category].asked += 1
    }
  })

  servedAnswers.forEach((answer) => {
    if (!answer.isCorrect) {
      return
    }

    const question = questionMap[answer.questionId]
    if (question) {
      categoryTotals[question.category].correct += 1
    }
  })

  const categoryBreakdown = (Object.keys(categoryTotals) as QuizCategory[])
    .filter((category) => categoryTotals[category].asked > 0)
    .map((category) => {
      const asked = categoryTotals[category].asked
      const correct = categoryTotals[category].correct
      return {
        category,
        asked,
        correct,
        percentage: Number(((correct / asked) * 100).toFixed(2))
      }
    })

  const missedQuestionIds = servedAnswers
    .filter((answer) => !answer.isCorrect)
    .map((answer) => answer.questionId)

  const mostMissedTerms = Array.from(
    new Set(missedQuestionIds.map((questionId) => questionMap[questionId]?.term).filter((term): term is string => Boolean(term)))
  )

  return {
    totalQuestionsServed: servedQuestionIds.length,
    totalCorrect,
    totalWrong,
    totalSkipped,
    percentageScore,
    rawScore,
    categoryBreakdown,
    missedQuestionIds,
    mostMissedTerms,
    performanceBand: getPerformanceBand(percentageScore)
  }
}

const GlossaryQuizPage: React.FC = () => {
  const { showInfo, showSuccess } = useToast()

  const [phase, setPhase] = useState<QuizPhase>('intro')
  const [sessionId, setSessionId] = useState('')
  const [shuffledQuestionIds, setShuffledQuestionIds] = useState<string[]>([])
  const [targetQuestionCount, setTargetQuestionCount] = useState<10 | 25>(10)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [strictMode, setStrictMode] = useState(false)
  const [answers, setAnswers] = useState<QuizAnswer[]>([])
  const [didContinueAfterTen, setDidContinueAfterTen] = useState(false)

  const [reviewQueue, setReviewQueue] = useState<string[]>([])
  const [reviewCursor, setReviewCursor] = useState(0)
  const [reviewSelectedOptionIndex, setReviewSelectedOptionIndex] = useState<number | null>(null)
  const [reviewEvaluated, setReviewEvaluated] = useState(false)

  const questionMap = useMemo(() => {
    return GLOSSARY_QUIZ_QUESTIONS.reduce<Record<string, GlossaryQuizQuestion>>((accumulator, question) => {
      accumulator[question.id] = question
      return accumulator
    }, {})
  }, [])

  const questionBankErrors = useMemo(() => validateQuestionBank(GLOSSARY_QUIZ_QUESTIONS), [])
  const isQuestionBankValid = questionBankErrors.length === 0

  const currentQuestionId = shuffledQuestionIds[currentIndex]
  const currentQuestion = currentQuestionId ? questionMap[currentQuestionId] : null
  const currentAnswer = currentQuestionId
    ? answers.find((answer) => answer.questionId === currentQuestionId)
    : undefined

  const checkpointQuestionIds = shuffledQuestionIds.slice(0, ROUND_ONE_COUNT)
  const servedQuestionIds = shuffledQuestionIds.slice(0, targetQuestionCount)

  const checkpointResult = useMemo(
    () => computeResult(checkpointQuestionIds, questionMap, answers, strictMode),
    [answers, checkpointQuestionIds, questionMap, strictMode]
  )

  const finalResult = useMemo(
    () => computeResult(servedQuestionIds, questionMap, answers, strictMode),
    [answers, questionMap, servedQuestionIds, strictMode]
  )

  const currentReviewQuestionId = reviewQueue[reviewCursor]
  const currentReviewQuestion = currentReviewQuestionId ? questionMap[currentReviewQuestionId] : null

  useEffect(() => {
    if (!isQuestionBankValid) {
      return
    }

    const storedSession = loadQuizSession()
    if (!storedSession) {
      return
    }

    const knownQuestionIds = new Set(GLOSSARY_QUIZ_QUESTIONS.map((question) => question.id))
    const isStoredSessionValid =
      storedSession.shuffledQuestionIds.length === SESSION_QUESTION_COUNT &&
      storedSession.shuffledQuestionIds.every((questionId) => knownQuestionIds.has(questionId))

    if (!isStoredSessionValid) {
      clearQuizSession()
      return
    }

    setSessionId(storedSession.sessionId)
    setShuffledQuestionIds(storedSession.shuffledQuestionIds)
    setTargetQuestionCount(storedSession.targetQuestionCount)
    setCurrentIndex(storedSession.currentIndex)
    setStrictMode(storedSession.strictMode)
    setAnswers(storedSession.answers)
    setDidContinueAfterTen(storedSession.targetQuestionCount === SESSION_QUESTION_COUNT)

    if (storedSession.phase === 'quiz' || storedSession.phase === 'checkpoint') {
      setPhase(storedSession.phase)
      showInfo('Resumed your in-progress quiz session.', 'Session restored')
      return
    }

    setPhase('intro')
  }, [isQuestionBankValid, showInfo])

  useEffect(() => {
    if (!sessionId || (phase !== 'quiz' && phase !== 'checkpoint')) {
      return
    }

    const persistedSession: PersistedQuizSession = {
      sessionId,
      shuffledQuestionIds,
      targetQuestionCount,
      currentIndex,
      strictMode,
      phase,
      answers
    }

    saveQuizSession(persistedSession)
  }, [answers, currentIndex, phase, sessionId, shuffledQuestionIds, strictMode, targetQuestionCount])

  const startNewQuiz = (): void => {
    if (!isQuestionBankValid) {
      return
    }

    const nextSessionId = `quiz_${Date.now()}`
    const nextQuestionIds = buildSessionQuestionIds(GLOSSARY_QUIZ_QUESTIONS)

    setSessionId(nextSessionId)
    setShuffledQuestionIds(nextQuestionIds)
    setTargetQuestionCount(10)
    setCurrentIndex(0)
    setAnswers([])
    setDidContinueAfterTen(false)
    setReviewQueue([])
    setReviewCursor(0)
    setReviewSelectedOptionIndex(null)
    setReviewEvaluated(false)
    setPhase('quiz')

    trackQuizEvent('quiz_started', {
      sessionId: nextSessionId,
      strictMode,
      startCount: ROUND_ONE_COUNT
    })
  }

  const handleSelectAnswer = (selectedOptionIndex: number): void => {
    if (!currentQuestion || currentAnswer) {
      return
    }

    const isCorrect = selectedOptionIndex === currentQuestion.correctOptionIndex
    const answer: QuizAnswer = {
      questionId: currentQuestion.id,
      selectedOptionIndex,
      isCorrect,
      skipped: false,
      answeredAt: new Date().toISOString()
    }

    setAnswers((previousAnswers) => [...previousAnswers, answer])

    trackQuizEvent('question_answered', {
      sessionId,
      questionId: currentQuestion.id,
      term: currentQuestion.term,
      category: currentQuestion.category,
      correct: isCorrect,
      skipped: false
    })

    trackQuizEvent(isCorrect ? 'answer_correct' : 'answer_wrong', {
      sessionId,
      questionId: currentQuestion.id
    })
  }

  const handleSkipQuestion = (): void => {
    if (!currentQuestion || currentAnswer) {
      return
    }

    const answer: QuizAnswer = {
      questionId: currentQuestion.id,
      selectedOptionIndex: null,
      isCorrect: false,
      skipped: true,
      answeredAt: new Date().toISOString()
    }

    setAnswers((previousAnswers) => [...previousAnswers, answer])

    trackQuizEvent('question_answered', {
      sessionId,
      questionId: currentQuestion.id,
      term: currentQuestion.term,
      category: currentQuestion.category,
      correct: false,
      skipped: true
    })

    trackQuizEvent('answer_skipped', {
      sessionId,
      questionId: currentQuestion.id
    })
  }

  const handleNextQuestion = (): void => {
    const reachedRoundEnd = currentIndex + 1 >= targetQuestionCount

    if (!reachedRoundEnd) {
      setCurrentIndex((previousIndex) => previousIndex + 1)
      return
    }

    if (targetQuestionCount === 10) {
      setPhase('checkpoint')
      return
    }

    setPhase('results')
    clearQuizSession()

    trackQuizEvent('quiz_completed', {
      sessionId,
      strictMode,
      servedQuestions: SESSION_QUESTION_COUNT,
      rawScore: finalResult.rawScore,
      percentageScore: finalResult.percentageScore
    })
  }

  const handleContinueToFullRun = (): void => {
    setTargetQuestionCount(SESSION_QUESTION_COUNT)
    setCurrentIndex(ROUND_ONE_COUNT)
    setDidContinueAfterTen(true)
    setPhase('quiz')
  }

  const handleStopAtCheckpoint = (): void => {
    setPhase('results')
    clearQuizSession()

    trackQuizEvent('quiz_completed', {
      sessionId,
      strictMode,
      servedQuestions: 10,
      rawScore: checkpointResult.rawScore,
      percentageScore: checkpointResult.percentageScore
    })
  }

  const handleRetry = (): void => {
    trackQuizEvent('retry_started', { previousSessionId: sessionId })
    startNewQuiz()
  }

  const handleRestartQuiz = (): void => {
    trackQuizEvent('retry_started', { previousSessionId: sessionId, sourcePhase: phase })
    startNewQuiz()
  }

  const handleGoToQuizHome = (): void => {
    setPhase('intro')
    setReviewQueue([])
    setReviewCursor(0)
    setReviewSelectedOptionIndex(null)
    setReviewEvaluated(false)
  }

  const handleStartReviewMode = (): void => {
    if (finalResult.missedQuestionIds.length === 0) {
      showInfo('No missed terms this round. Retry to practice with a fresh shuffle.')
      return
    }

    setReviewQueue([...finalResult.missedQuestionIds])
    setReviewCursor(0)
    setReviewSelectedOptionIndex(null)
    setReviewEvaluated(false)
    setPhase('review')

    trackQuizEvent('study_mode_started', {
      sessionId,
      missedCount: finalResult.missedQuestionIds.length
    })
  }

  const handleReviewAnswer = (selectedOptionIndex: number): void => {
    if (!currentReviewQuestion || reviewEvaluated) {
      return
    }

    setReviewSelectedOptionIndex(selectedOptionIndex)
    setReviewEvaluated(true)
  }

  const handleReviewSkip = (): void => {
    if (!currentReviewQuestion || reviewEvaluated) {
      return
    }

    setReviewSelectedOptionIndex(null)
    setReviewEvaluated(true)
  }

  const handleNextReviewQuestion = (): void => {
    if (!currentReviewQuestion) {
      return
    }

    const isCorrect = reviewSelectedOptionIndex === currentReviewQuestion.correctOptionIndex

    setReviewQueue((previousQueue) => {
      const workingQueue = [...previousQueue]
      if (!isCorrect) {
        workingQueue.push(currentReviewQuestion.id)
      }
      return workingQueue
    })

    setReviewCursor((previousCursor) => previousCursor + 1)
    setReviewSelectedOptionIndex(null)
    setReviewEvaluated(false)
  }

  useEffect(() => {
    if (phase !== 'review') {
      return
    }

    if (reviewCursor < reviewQueue.length) {
      return
    }

    setPhase('results')
    setReviewQueue([])
    setReviewCursor(0)
    setReviewSelectedOptionIndex(null)
    setReviewEvaluated(false)
    showSuccess('Study mode complete. You answered all missed terms correctly at least once.', 'Review complete')
  }, [phase, reviewCursor, reviewQueue.length, showSuccess])

  useEffect(() => {
    if (phase !== 'quiz' && phase !== 'review') {
      return
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (phase === 'quiz') {
        if (!currentQuestion || currentAnswer) {
          return
        }

        if (['1', '2', '3', '4'].includes(event.key)) {
          event.preventDefault()
          handleSelectAnswer(Number(event.key) - 1)
        }

        if (event.key.toLowerCase() === 's') {
          event.preventDefault()
          handleSkipQuestion()
        }

        return
      }

      if (!currentReviewQuestion || reviewEvaluated) {
        return
      }

      if (['1', '2', '3', '4'].includes(event.key)) {
        event.preventDefault()
        handleReviewAnswer(Number(event.key) - 1)
      }

      if (event.key.toLowerCase() === 's') {
        event.preventDefault()
        handleReviewSkip()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [
    currentAnswer,
    currentQuestion,
    currentReviewQuestion,
    phase,
    reviewEvaluated
  ])

  useEffect(() => {
    if (!currentAnswer || phase !== 'quiz') {
      return
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter') {
        return
      }
      event.preventDefault()
      handleNextQuestion()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [currentAnswer, phase, targetQuestionCount, currentIndex])

  useEffect(() => {
    if (!reviewEvaluated || phase !== 'review') {
      return
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter') {
        return
      }
      event.preventDefault()
      handleNextReviewQuestion()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [phase, reviewEvaluated, reviewSelectedOptionIndex])

  return (
    <div className="min-h-screen bg-background-light">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
        {phase !== 'intro' && (
          <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/data-dictionary-quiz"
              onClick={handleGoToQuizHome}
              className="inline-flex items-center gap-2 text-secondary-500 transition-colors hover:text-secondary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Quiz Home
            </Link>

            {phase !== 'results' && (
              <button
                type="button"
                onClick={handleRestartQuiz}
                className="inline-flex items-center gap-2 rounded-ms border border-secondary-300 px-4 py-2 text-sm font-medium text-secondary-700 transition-colors hover:bg-secondary-100"
              >
                <RotateCcw className="h-4 w-4" />
                Restart quiz
              </button>
            )}
          </div>
        )}

        {!isQuestionBankValid && (
          <div className="card border border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />
              <div>
                <h1 className="mb-3 text-xl font-bold text-text-primary">Question Bank Validation Error</h1>
                <ul className="list-disc space-y-1 pl-5 text-sm text-red-700">
                  {questionBankErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isQuestionBankValid && phase === 'intro' && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-ms border border-secondary-200 bg-white p-6 shadow-sm sm:p-8"
          >
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary-100 blur-2xl"
              animate={{ x: [0, -12, 0], y: [0, 10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-12 -left-12 h-52 w-52 rounded-full bg-secondary-100 blur-2xl"
              animate={{ x: [0, 14, 0], y: [0, -8, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />

            <div className="relative z-10 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-ms border border-secondary-200 bg-secondary-50 p-3">
                <a
                  href="https://docs.momentscience.com/glossary"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-secondary-700 transition hover:text-secondary-500"
                >
                  Open glossary docs
                  <ExternalLink className="h-4 w-4" />
                </a>
                <p className="text-xs text-text-neutral">Keyboard: 1-4 answer, S skip, Enter next.</p>
              </div>

              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05, duration: 0.35 }}
                  className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  Partner Self-Assessment
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.45 }}
                >
                  <h1 className="mb-3 text-h2 text-text-primary">MomentScience Glossary Quiz</h1>
                  <p className="max-w-2xl text-text-neutral">
                    Learn glossary terms with scenario-based cards, instant explanations, and a guided 10-question checkpoint before the full run.
                  </p>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.45 }}
                className="rounded-ms border border-primary-200 bg-primary-50 p-5"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-primary-700">Quick start</p>
                    <p className="mt-1 text-sm text-primary-700">Start with 10 questions, then choose to continue to 25.</p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={startNewQuiz}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center justify-center rounded-ms bg-primary-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-primary-700"
                  >
                    Start quiz now
                  </motion.button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.45 }}
                className="grid grid-cols-1 gap-3 sm:grid-cols-3"
              >
                <div className="rounded-ms border border-secondary-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary-600">Round 1</p>
                  <p className="mt-1 text-sm text-text-neutral">10 mixed questions</p>
                </div>
                <div className="rounded-ms border border-secondary-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary-600">Continue</p>
                  <p className="mt-1 text-sm text-text-neutral">Finish all 25 terms</p>
                </div>
                <div className="rounded-ms border border-secondary-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary-600">Study Mode</p>
                  <p className="mt-1 text-sm text-text-neutral">Repeat missed terms</p>
                </div>
              </motion.div>

              <label className="flex items-center gap-3 text-sm font-medium text-text-primary">
                <input
                  type="checkbox"
                  checked={strictMode}
                  onChange={(event) => setStrictMode(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Enable strict mode (wrong answers score -0.25)
              </label>
            </div>
          </motion.section>
        )}

        {phase === 'quiz' && currentQuestion && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="card">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold text-secondary-500">
                  Question {currentIndex + 1} of {targetQuestionCount}
                </p>
                <p className="text-sm text-text-neutral">
                  Correct: {finalResult.totalCorrect} | Wrong: {finalResult.totalWrong} | Skipped: {finalResult.totalSkipped}
                </p>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary-100">
                <div
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${((currentIndex + 1) / targetQuestionCount) * 100}%` }}
                />
              </div>
            </div>

            <div className="rounded-ms border border-secondary-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700">
                  <BookOpen className="h-3.5 w-3.5" />
                  {categoryLabels[currentQuestion.category]}
                </div>
                <div className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                  {currentQuestion.difficulty}
                </div>
              </div>

              <div className="mb-5 rounded-ms border border-secondary-200 bg-secondary-50 p-4">
                <h2 className="mb-2 text-h3 text-text-primary">{currentQuestion.term}</h2>
                <p className="text-text-neutral">{currentQuestion.questionText}</p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Answer options">
                {currentQuestion.options.map((option, index) => {
                  const isSelected = currentAnswer?.selectedOptionIndex === index
                  const isCorrectOption = currentAnswer && index === currentQuestion.correctOptionIndex
                  const isWrongSelected = currentAnswer && isSelected && !isCorrectOption

                  return (
                    <button
                      key={`${currentQuestion.id}_${index}`}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={Boolean(currentAnswer)}
                      onClick={() => handleSelectAnswer(index)}
                      className={`w-full rounded-ms border p-4 text-left shadow-sm transition ${
                        isCorrectOption
                          ? 'border-green-500 bg-green-50'
                          : isWrongSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-secondary-200 bg-white hover:-translate-y-0.5 hover:border-primary-500 hover:bg-primary-50'
                      }`}
                    >
                      <div className="mb-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-secondary-300 bg-secondary-50 text-xs font-semibold text-secondary-700">
                        {index + 1}
                      </div>
                      <p className="text-sm text-text-neutral">{option}</p>
                    </button>
                  )
                })}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {!currentAnswer && (
                  <button
                    type="button"
                    onClick={handleSkipQuestion}
                    className="inline-flex items-center gap-2 rounded-ms border border-secondary-300 px-4 py-2 text-secondary-700 transition-colors hover:bg-secondary-100"
                  >
                    <SkipForward className="h-4 w-4" />
                    Skip question
                  </button>
                )}

                {currentAnswer && (
                  <button type="button" onClick={handleNextQuestion} className="btn-primary">
                    {currentIndex + 1 >= targetQuestionCount ? 'Finish round' : 'Next question'}
                  </button>
                )}
              </div>

              {currentAnswer && (
                <div
                  className={`mt-6 rounded-ms border p-4 ${
                    currentAnswer.skipped
                      ? 'border-amber-300 bg-amber-50'
                      : currentAnswer.isCorrect
                        ? 'border-green-300 bg-green-50'
                        : 'border-red-300 bg-red-50'
                  }`}
                  aria-live="polite"
                >
                  <div className="mb-2 flex items-center gap-2">
                    {currentAnswer.skipped && <HelpCircle className="h-5 w-5 text-amber-700" />}
                    {!currentAnswer.skipped && currentAnswer.isCorrect && <CheckCircle2 className="h-5 w-5 text-green-700" />}
                    {!currentAnswer.skipped && !currentAnswer.isCorrect && <XCircle className="h-5 w-5 text-red-700" />}
                    <p className="text-sm font-semibold text-text-primary">
                      {currentAnswer.skipped ? 'Skipped' : currentAnswer.isCorrect ? 'Correct' : 'Not correct'}
                    </p>
                  </div>
                  <p className="text-sm text-text-neutral">{currentQuestion.explanation}</p>
                </div>
              )}
            </div>
          </motion.section>
        )}

        <AnimatePresence>
          {phase === 'checkpoint' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
            >
              <motion.div
                initial={{ scale: 0.95, y: 12 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 12 }}
                className="card w-full max-w-xl"
              >
                <h2 className="mb-3 text-h3 text-text-primary">Checkpoint reached</h2>
                <p className="mb-4 text-text-neutral">You completed 10 questions. Continue to the full 25-question run?</p>

                <div className="mb-6 rounded-ms border border-secondary-200 bg-white p-4">
                  <p className="text-sm text-text-neutral">Checkpoint score</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {checkpointResult.rawScore.toFixed(2)} / {ROUND_ONE_COUNT} ({checkpointResult.percentageScore.toFixed(2)}%)
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button type="button" onClick={handleContinueToFullRun} className="btn-primary">
                    Continue to 25
                  </button>
                  <button
                    type="button"
                    onClick={handleStopAtCheckpoint}
                    className="rounded-ms border border-secondary-300 px-5 py-3 text-secondary-700 transition-colors hover:bg-secondary-100"
                  >
                    Stop and view results
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {phase === 'results' && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="card">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-h2 text-text-primary">Quiz Results</h1>
                  <p className="text-text-neutral">{finalResult.performanceBand}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-ms border border-secondary-200 bg-white p-4">
                  <p className="mb-1 text-xs uppercase tracking-wide text-text-neutral">Final score</p>
                  <p className="text-3xl font-bold text-text-primary">
                    {finalResult.rawScore.toFixed(2)} / {finalResult.totalQuestionsServed}
                  </p>
                  <p className="text-sm text-text-neutral">{finalResult.percentageScore.toFixed(2)}%</p>
                </div>
                <div className="rounded-ms border border-secondary-200 bg-white p-4">
                  <p className="mb-1 text-xs uppercase tracking-wide text-text-neutral">Answer summary</p>
                  <p className="text-sm text-text-neutral">Correct: {finalResult.totalCorrect}</p>
                  <p className="text-sm text-text-neutral">Wrong: {finalResult.totalWrong}</p>
                  <p className="text-sm text-text-neutral">Skipped: {finalResult.totalSkipped}</p>
                </div>
              </div>

              {didContinueAfterTen && (
                <div className="mt-4 rounded-ms border border-primary-200 bg-primary-50 p-4">
                  <p className="text-sm font-semibold text-primary-700">Checkpoint score (first 10)</p>
                  <p className="text-sm text-primary-700">
                    {checkpointResult.rawScore.toFixed(2)} / {ROUND_ONE_COUNT} ({checkpointResult.percentageScore.toFixed(2)}%)
                  </p>
                </div>
              )}
            </div>

            <div className="card">
              <h2 className="mb-3 text-h3 text-text-primary">Category Breakdown</h2>
              <div className="space-y-3">
                {finalResult.categoryBreakdown.map((item) => (
                  <div key={item.category} className="rounded-ms border border-secondary-200 bg-white p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="font-medium text-text-primary">{categoryLabels[item.category]}</p>
                      <p className="text-sm text-text-neutral">{item.correct}/{item.asked}</p>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-secondary-100">
                      <div className="h-full bg-secondary-500" style={{ width: `${item.percentage}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h2 className="mb-3 text-h3 text-text-primary">Most Missed Terms</h2>
              {finalResult.mostMissedTerms.length > 0 ? (
                <ul className="list-disc space-y-1 pl-5 text-text-neutral">
                  {finalResult.mostMissedTerms.map((term) => (
                    <li key={term}>{term}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-text-neutral">No missed terms in this run. Great work.</p>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={handleRetry} className="btn-primary inline-flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                Retry quiz
              </button>
              <button
                type="button"
                onClick={handleStartReviewMode}
                className="rounded-ms border border-secondary-300 px-5 py-3 text-secondary-700 transition-colors hover:bg-secondary-100"
              >
                Study missed terms
              </button>
            </div>
          </motion.section>
        )}

        {phase === 'review' && currentReviewQuestion && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="card">
              <p className="text-sm font-semibold text-secondary-500">Study mode</p>
              <h1 className="text-h3 text-text-primary">Missed Terms Review</h1>
              <p className="mt-2 text-text-neutral">Keep going until each missed term is answered correctly once.</p>
            </div>

            <div className="rounded-ms border border-secondary-200 bg-white p-5 shadow-sm">
              <div className="mb-4">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700">
                  <BookOpen className="h-3.5 w-3.5" />
                  {categoryLabels[currentReviewQuestion.category]} • {currentReviewQuestion.difficulty}
                </div>
                <h2 className="mb-2 text-h3 text-text-primary">{currentReviewQuestion.term}</h2>
                <p className="text-text-neutral">{currentReviewQuestion.questionText}</p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {currentReviewQuestion.options.map((option, index) => {
                  const isSelected = reviewSelectedOptionIndex === index
                  const isCorrectOption = reviewEvaluated && index === currentReviewQuestion.correctOptionIndex
                  const isWrongSelected = reviewEvaluated && isSelected && !isCorrectOption

                  return (
                    <button
                      key={`${currentReviewQuestion.id}_${index}`}
                      type="button"
                      onClick={() => handleReviewAnswer(index)}
                      disabled={reviewEvaluated}
                      className={`w-full rounded-ms border p-4 text-left shadow-sm transition ${
                        isCorrectOption
                          ? 'border-green-500 bg-green-50'
                          : isWrongSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-secondary-200 bg-white hover:-translate-y-0.5 hover:border-primary-500 hover:bg-primary-50'
                      }`}
                    >
                      <div className="mb-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-secondary-300 bg-secondary-50 text-xs font-semibold text-secondary-700">
                        {index + 1}
                      </div>
                      <p className="text-sm text-text-neutral">{option}</p>
                    </button>
                  )
                })}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {!reviewEvaluated && (
                  <button
                    type="button"
                    onClick={handleReviewSkip}
                    className="inline-flex items-center gap-2 rounded-ms border border-secondary-300 px-4 py-2 text-secondary-700 transition-colors hover:bg-secondary-100"
                  >
                    <SkipForward className="h-4 w-4" />
                    Skip for now
                  </button>
                )}

                {reviewEvaluated && (
                  <button type="button" onClick={handleNextReviewQuestion} className="btn-primary">
                    Next study question
                  </button>
                )}
              </div>

              {reviewEvaluated && (
                <div className="mt-6 rounded-ms border border-secondary-200 bg-secondary-50 p-4" aria-live="polite">
                  <p className="mb-1 text-sm font-semibold text-text-primary">
                    {reviewSelectedOptionIndex === currentReviewQuestion.correctOptionIndex
                      ? 'Correct in study mode'
                      : 'Not correct, this term will appear again'}
                  </p>
                  <p className="text-sm text-text-neutral">{currentReviewQuestion.explanation}</p>
                </div>
              )}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  )
}

export default GlossaryQuizPage
